<script>
    /* States */
    import {
        explorerState
    } from '@app/store/explorerStore.svelte.js';
    import { setExternalDataState } from "@app/store/externalDataStore.svelte.js";
    import { authState } from "@app/store/authStore.svelte.js";
    import { projectState, updateProjectId } from "@app/store/projectStore.svelte.js";
    import { metadataState } from "@app/store/metadataStore.svelte.js";
    import { speakerState } from "@app/store/speakerStore.svelte.js";
    import { issueState } from "@app/store/issueStore.svelte.js";
    import { markerState } from "@app/store/markerStore.svelte.js";
    import { eventGroupState } from "@app/store/eventGroupStore.svelte.js";
    import { styleState } from "@app/store/styleStore.svelte.js";
    import { Circle } from "svelte-loading-spinners";
    import { toast } from "@zerodevx/svelte-toast";
    import { showModal, hideModal } from "@app/store/modalStore.svelte.js";
    import {
    establishHistoryBaseline
} from "@app/store/historyStore.svelte.js";
    import Swal from "sweetalert2";
    import { SWAL_BASE, SWAL_CLASSES } from "@app/lib/swalClasses.js";
    import { saveLastExplorerLocationFromRecord } from "@app/lib/explorerLastLocation.js";
    import { ccprjFunc } from '@app/lib/ccprj.js';
    import * as m from "@app/paraglide/messages.js";
    
    /* Firebase */
    import { auth, functions } from "@app/configs/firebase.js";
    import db from "@app/configs/firestore.js";
    import storage from "@app/configs/storage.js";
    import { collection, doc, getDoc, getDocs, setDoc, updateDoc, query, where, orderBy, limit, Timestamp } from "firebase/firestore";
    import { ref, uploadString, getDownloadURL } from "firebase/storage";
    import { httpsCallable } from "firebase/functions";

    const iconMap = {
        "folder" : "bi-folder-fill",
        "project" : "bi-file-earmark-play",
        "team project" : "bi-people",
        "work order" : "bi-clipboard-check"
    }
    const taskRecordTypes = ["project", "team project", "work order"];

    let userId = auth.currentUser.uid;
    let userEmail = auth.currentUser.email;
    let teamId = authState.team ? authState.team.id : null;
    let homeDbRef = collection(db, "users", userId, "storage");
    let teamDbRef = teamId ? collection(db, "teams", teamId, "storage") : null;
    let storageRef = ref(storage);
    let homeStorageBasePath = `users/${userId}/projects/`;
    let teamStorageBasePath = "teams/" + teamId + "/storage/";
    let operationLoading = $state(false); // For specific operations like overwrite, move
    let currentDragTarget = null;

    // Helper function to check if a folder should be visible to current user based on group permissions
    function canUserAccessFolder(folder) {
        // Admin and Manager roles always have access to all folders
        if (authState.user && (authState.user.role === "admin" || authState.user.role === "manager")) {
            return true;
        }
        
        // If folder has no groups assigned, it's accessible to all team members
        if (!folder.groups || folder.groups.length === 0) {
            return true;
        }
        
        // Check if user is a member of any of the folder's assigned groups
        if (authState.user && authState.user.groups) {
            return folder.groups.some(groupId => authState.user.groups.includes(groupId));
        }
        
        // If user has no groups, they can't access group-restricted folders
        return false;
    }

    // Helper function to check if a project record should be visible based on its parent folder permissions
    async function canUserAccessRecord(record) {
        // Personal records are always accessible
        if (record.rootDir === "personal") {
            return true;
        }
        
        // If record has no locationId, it's at root level and accessible
        if (!record.locationId) {
            return true;
        }
        
        // For team records with locationId, check parent folder permissions
        if (record.rootDir === "team" && teamDbRef) {
            try {
                const parentFolderDoc = await getDoc(doc(teamDbRef, record.locationId));
                
                // If parent folder doesn't exist, don't show the record
                if (!parentFolderDoc.exists()) {
                    return false;
                }
                
                const parentFolder = parentFolderDoc.data();
                return canUserAccessFolder(parentFolder);
            } catch (error) {
                console.error("Error checking parent folder permissions:", error);
                return false;
            }
        }
        
        return true;
    }

    // Separate folders from other records for the thumbnail view
    let folders = $derived(explorerState.records.filter(record => record.type === "folder"));
    let otherRecords = $derived(explorerState.records.filter(record => record.type !== "folder"));

    /* Get records where folder Id is the records locationId */
    async function getRecords(folderId = null){
        try {
            explorerState.records = [];
            explorerState.selectedRecords = [];

            // console.log("Getting Records for Folder ID: " + folderId);
            
            // Handle special rootDir values
            if (explorerState.rootDir === "recent") {
                // Fetch recent records from both personal and team storage
                let recentRecords = [];
                
                // Personal storage
                let personalSnapshot = await getDocs(query(homeDbRef,
                    orderBy("updatedOn", "desc"),
                    limit(100)));
                    
                personalSnapshot.forEach((doc) => {
                    recentRecords.push({...(doc.data()), rootDir: "personal"});
                });
                
                // Team storage (if available)
                if (teamDbRef) {
                    let teamSnapshot = await getDocs(query(teamDbRef,
                        orderBy("updatedOn", "desc"),
                        limit(100)));
                        
                    teamSnapshot.forEach((doc) => {
                        recentRecords.push({...(doc.data()), rootDir: "team"});
                    });
                }
                
                // First, separate folders from other records
                const folders = recentRecords.filter(record => record.type === "folder");
                const nonFolders = recentRecords.filter(record => record.type !== "folder");
                
                // Sort each group by updatedOn timestamp
                folders.sort((a, b) => {
                    // Convert to milliseconds for comparison
                    const timeA = a.updatedOn ? a.updatedOn.toMillis() : 0;
                    const timeB = b.updatedOn ? b.updatedOn.toMillis() : 0;
                    return timeB - timeA; // Newest first
                });
                
                nonFolders.sort((a, b) => {
                    // Convert to milliseconds for comparison
                    const timeA = a.updatedOn ? a.updatedOn.toMillis() : 0;
                    const timeB = b.updatedOn ? b.updatedOn.toMillis() : 0;
                    return timeB - timeA; // Newest first
                });
                
                // Combine: folders first, then other records
                // Filter team folders by permissions
                const filteredFolders = folders.filter(folder => {
                    // If personal folder, keep it
                    if (folder.rootDir === "personal") {
                        return true;
                    }
                    // If team folder, check permissions
                    else if (folder.rootDir === "team") {
                        return canUserAccessFolder(folder);
                    }
                    return false;
                });
                
                // Filter non-folders (projects) by checking parent folder permissions
                const filteredNonFolders = [];
                for (const record of nonFolders) {
                    if (await canUserAccessRecord(record)) {
                        filteredNonFolders.push(record);
                    }
                }

                explorerState.records = [...filteredFolders, ...filteredNonFolders].slice(0, 100);
                
            } else if (explorerState.rootDir === "starred") {
                // Fetch starred records from both personal and team storage
                let starredRecords = [];
                
                // Personal storage
                let personalSnapshot = await getDocs(query(homeDbRef,
                    where("star", "==", true),
                    limit(100)));
                    
                personalSnapshot.forEach((doc) => {
                    starredRecords.push({...(doc.data()), rootDir: "personal"});
                });
                
                // Team storage (if available)
                if (teamDbRef) {
                    let teamSnapshot = await getDocs(query(teamDbRef,
                        where("star", "==", true),
                        limit(100)));
                        
                    teamSnapshot.forEach((doc) => {
                        starredRecords.push({...(doc.data()), rootDir: "team"});
                    });
                }
                
                // First, separate folders from other records
                const folders = starredRecords.filter(record => record.type === "folder");
                const nonFolders = starredRecords.filter(record => record.type !== "folder");
                
                // Sort each group by updatedOn timestamp
                folders.sort((a, b) => {
                    // Convert to milliseconds for comparison
                    const timeA = a.updatedOn ? a.updatedOn.toMillis() : 0;
                    const timeB = b.updatedOn ? b.updatedOn.toMillis() : 0;
                    return timeB - timeA; // Newest first
                });
                
                nonFolders.sort((a, b) => {
                    // Convert to milliseconds for comparison
                    const timeA = a.updatedOn ? a.updatedOn.toMillis() : 0;
                    const timeB = b.updatedOn ? b.updatedOn.toMillis() : 0;
                    return timeB - timeA; // Newest first
                });
                
                // Combine: folders first, then other records
                // Filter team folders by permissions
                const filteredFolders = folders.filter(folder => {
                    // If personal folder, keep it
                    if (folder.rootDir === "personal") {
                        return true;
                    }
                    // If team folder, check permissions
                    else if (folder.rootDir === "team") {
                        return canUserAccessFolder(folder);
                    }
                    return false;
                });
                
                // Filter non-folders (projects) by checking parent folder permissions
                const filteredNonFolders = [];
                for (const record of nonFolders) {
                    if (await canUserAccessRecord(record)) {
                        filteredNonFolders.push(record);
                    }
                }

                explorerState.records = [...filteredFolders, ...filteredNonFolders].slice(0, 100);
                
            } else if (explorerState.rootDir === "tasks") {
                let taskRecords = [];

                let personalSnapshot = await getDocs(query(homeDbRef,
                    where("assignedTo", "==", userEmail),
                    limit(100)));

                personalSnapshot.forEach((doc) => {
                    const record = doc.data();
                    const recordId = record.id || doc.id;
                    taskRecords.push({
                        ...record,
                        id: recordId,
                        rootDir: "personal",
                        uniqueId: `personal-${recordId}`
                    });
                });

                if (teamDbRef) {
                    let teamSnapshot = await getDocs(query(teamDbRef,
                        where("assignedTo", "==", userEmail),
                        limit(100)));

                    teamSnapshot.forEach((doc) => {
                        const record = doc.data();
                        const recordId = record.id || doc.id;
                        taskRecords.push({
                            ...record,
                            id: recordId,
                            rootDir: "team",
                            uniqueId: `team-${recordId}`
                        });
                    });
                }

                taskRecords = taskRecords.filter(record => taskRecordTypes.includes(record.type) && record.state !== "Archived");

                const filteredRecords = [];
                for (const record of taskRecords) {
                    if (await canUserAccessRecord(record)) {
                        filteredRecords.push(record);
                    }
                }

                filteredRecords.sort((a, b) => {
                    const timeA = a.updatedOn ? a.updatedOn.toMillis() : 0;
                    const timeB = b.updatedOn ? b.updatedOn.toMillis() : 0;
                    return timeB - timeA;
                });

                explorerState.records = filteredRecords.slice(0, 100);

            } else {
                // Normal folder query for personal or team storage
                let querySnapshot = await getDocs(query(explorerState.rootDir === "personal" ? homeDbRef : teamDbRef, where("locationId", "==", folderId), orderBy("type"), orderBy("name", "asc")));

                querySnapshot.forEach((doc) => {
                    const record = {...(doc.data()), rootDir: explorerState.rootDir};
                    
                    // For personal storage, add all records
                    if (explorerState.rootDir === "personal") {
                        explorerState.records = [...explorerState.records, record];
                    }
                    // For team storage, filter folders by permissions
                    else if (explorerState.rootDir === "team") {
                        // If it's a folder, check permissions
                        if (record.type === "folder") {
                            if (canUserAccessFolder(record)) {
                                explorerState.records = [...explorerState.records, record];
                            }
                        } 
                        // Non-folder records are always shown (they get filtered at the folder level)
                        else {
                            explorerState.records = [...explorerState.records, record];
                        }
                    }
                });
            }
        } catch(err){
            // console.error(err);
            toast.push(err.message, {
                classes: ["toast-danger"],
            });
        }   
    }

    async function openRecord(id) {
        // console.log("Opening record with id: ", id);
        let record = explorerState.records.find(record => record.id === id);

        // Handle folder navigation
        if (record.type === "folder") {
            // console.log(`Opening folder: ${record.name}, current rootDir: ${explorerState.rootDir}`);
            
            // Check if we're in a system result view
            if (explorerState.rootDir === "recent" || explorerState.rootDir === "starred" || explorerState.rootDir === "tasks") {
                // console.log(`Switching from ${explorerState.rootDir} view to ${record.rootDir} view`);
                
                // Switch to the appropriate rootDir based on the folder's rootDir
                explorerState.rootDir = record.rootDir;
                
                // Build the navigation path by finding all parent folders
                await buildFolderNavigationPath(record);
            } else {
                // Regular folder navigation in normal view
                explorerState.navHistory = [...explorerState.navHistory, record];
            }
            
            // Navigate to the folder contents
            getRecords(record.id);
        } 
        // Handle projects differently based on context
        else if (record.type === "project" || record.type === "team project" || record.type === "work order") {
            // If in save mode, overwrite the project instead of opening it
            if (explorerState.context === "save") {
                overwriteProject(record);
            } else {
                // In load mode, open the project as before
                loadProject(record);
            }
        } 
    }
    
    // Build navigation path for a folder by finding all parent folders
    async function buildFolderNavigationPath(folder) {
        try {
            operationLoading = true;
            
            // Clear current navigation history
            explorerState.navHistory = [];
            
            // If no folder, we're at root
            if (!folder) {
                return;
            }
            
            // Select the appropriate database reference
            const dbRef = folder.rootDir === "personal" ? homeDbRef : teamDbRef;
            if (!dbRef) {
                throw new Error(`Invalid database reference for rootDir: ${folder.rootDir}`);
            }
            
            // First add the current folder to the path
            let folderPath = [];
            
            // If folder has no locationId, it's at root level already
            if (!folder.locationId) {
                explorerState.navHistory = [folder];
                return;
            }
            
            // Start with the current folder's locationId to find its parent
            let currentFolderId = folder.locationId;
            let pathComplete = false;
            let safetyCounter = 0; // Prevent infinite loops
            const MAX_DEPTH = 20; // Maximum folder nesting depth
            
            // Keep traversing up until we reach the root or hit safety limit
            while (currentFolderId && !pathComplete && safetyCounter < MAX_DEPTH) {
                safetyCounter++;
                
                try {
                    // Get the parent folder
                    const parentDoc = await getDoc(doc(dbRef, currentFolderId));
                    
                    if (parentDoc.exists()) {
                        const parentData = parentDoc.data();
                        const parentFolder = { ...parentData, rootDir: folder.rootDir };
                        
                        // Add to path (at the beginning)
                        folderPath.unshift(parentFolder);
                        
                        // Continue with parent's parent if it exists
                        if (parentFolder.locationId) {
                            currentFolderId = parentFolder.locationId;
                        } else {
                            pathComplete = true;
                        }
                    } else {
                        pathComplete = true;
                    }
                } catch (err) {
                    // console.error(`Error fetching parent folder: ${err.message}`);
                    pathComplete = true;
                }
            }
            
            // Add the original folder at the end
            folderPath.push(folder);
            
            // Update navigation history with the complete path
            explorerState.navHistory = folderPath;
            
        } catch (error) {
            // console.error("Error building folder navigation path:", error);
            toast.push(`Failed to build folder path: ${error.message}`, {
                classes: ["toast-danger"],
            });
        } finally {
            operationLoading = false;
        }
    }
    
    async function overwriteProject(record) {
        if (operationLoading) return;
        
        try {
            operationLoading = true;
            
            // Check if record is locked by someone else
            if (record.locked && record.lockedBy !== userEmail) {
                throw new Error(m.explorer_thumbnail_overwrite_locked_error({ name: record.name, lockedBy: record.lockedBy }));
            }
            
            // Show confirmation dialog
            const result = await Swal.fire({
                ...SWAL_BASE,
                customClass: SWAL_CLASSES.destructiveDeny,
                titleText: m.explorer_thumbnail_overwrite_title(),
                text: m.explorer_thumbnail_overwrite_text({ name: record.name }),
                showDenyButton: true,
                showCancelButton: false,
                confirmButtonText: m.explorer_thumbnail_overwrite_confirm(),
                denyButtonText: m.explorer_thumbnail_cancel_button(),
                allowOutsideClick: false,
                allowEscapeKey: false,
            });
            
            if (!result.isConfirmed) {
                throw new Error(m.explorer_thumbnail_overwrite_cancelled_error());
            }
            
            // Update the project to use the same ID as the selected record
            updateProjectId(record.id);
            
            // Determine file path based on storage location
            const projectFilePath = record.rootDir === "team"
                ? teamStorageBasePath + record.id
                : homeStorageBasePath + record.id;
                
            // Create project JSON
            const projectJson = ccprjFunc.createCcprj({
                project: JSON.parse(JSON.stringify(projectState)),
                metadata: JSON.parse(JSON.stringify(metadataState)),
                speakers: JSON.parse(JSON.stringify(speakerState)),
                issues: JSON.parse(JSON.stringify(issueState)),
                markers: JSON.parse(JSON.stringify(markerState)),
                eventGroups: JSON.parse(JSON.stringify(eventGroupState)),
                style: JSON.parse(JSON.stringify(styleState))
            });

            projectJson.rootDir = explorerState.rootDir;
            
            // Upload to storage
            const uploadProjectRef = ref(storage, projectFilePath);
            await uploadString(uploadProjectRef, JSON.stringify(projectJson));
            
            // Update project record in database
            const projectInfo = {
                id: record.id,
                name: projectState.name,
                type: projectState.type === "team" ? "team project" : "project",
                owner: userEmail,
                createdOn: record.createdOn,
                updatedOn: Timestamp.fromDate(new Date()),
                locationId: record.locationId,
                shared: record.rootDir === "team" ? true : false,
                star: record.star || false,
                description: record.description || "",
                rootDir: record.rootDir,
                assignedTo: record.assignedTo ?? null,
                state: record.state ?? null,
                locked: record.locked || false,
                lockedBy: record.lockedBy ?? null,
                lockedOn: record.lockedOn ?? null,
                dueDate: record.dueDate ?? null,
                priority: record.priority ?? null,
                totalEvents: record.totalEvents ?? null,
                eventGroups: record.eventGroups ?? null,
            };
            
            const dbRef = record.rootDir === "team" ? teamDbRef : homeDbRef;
            await setDoc(doc(dbRef, projectInfo.id), projectInfo);
            
            // Update project state with location info
            projectState.folderId = record.locationId;
            projectState.rootDir = record.rootDir;
            
            // Reset history and show success message
            establishHistoryBaseline({ name: "save project" });
            toast.push(m.explorer_thumbnail_overwrite_success_toast({ name: projectState.name }), {
                classes: ["toast-success"],
            });
            
            // Update the record in the records list
            explorerState.records = explorerState.records.map(r => 
                r.id === record.id ? projectInfo : r
            );

            saveLastExplorerLocationFromRecord(userId, record);
            
            hideModal();
        } catch (error) {
            // console.error("Error overwriting project:", error);
            toast.push(m.explorer_thumbnail_overwrite_failed_toast({ error: error.message }), {
                classes: ["toast-danger"],
            });
        } finally {
            operationLoading = false;
        }
    }

    async function loadProject(record) {
        try {
            operationLoading = true;
            
            // Check if project is locked by someone else
            if (record.locked && record.lockedBy !== userEmail) {
                // Show notification about locked project
                await Swal.fire({
                    ...SWAL_BASE,
                    customClass: SWAL_CLASSES.ok,
                    title: m.explorer_thumbnail_locked_title(),
                    text: m.explorer_thumbnail_locked_text({ lockedBy: record.lockedBy }),
                    icon: 'info',
                    confirmButtonText: m.explorer_thumbnail_locked_confirm(),
                });
            }
            
            // Determine the storage path based on whether it's personal or team
            const projectFilePath = record.rootDir === "team" 
                ? `${teamStorageBasePath}${record.id}` 
                : `${homeStorageBasePath}${record.id}`;
            
            // Get the URL for the file
            let url;
            if (record.rootDir === "team") {
                const result = await httpsCallable(functions, "v8GetDownloadLink")(projectFilePath);
                url = result.data;
            } else {
                url = await getDownloadURL(ref(storageRef, projectFilePath));
            }
            
            // Fetch the file content
            const response = await fetch(url);
            const fileData = await response.json();
            fileData.rootDir = record.rootDir;
            
            // Process based on record type
            if (record.type === "work order") {
                openWorkOrder(fileData, record);
            } else if (record.type === "project") {
                openProjectFile(fileData, record);
            } else if (record.type === "team project") {
                openTeamProjectFile(fileData, record);
            }
            
        } catch (error) {
            // console.error(`Error opening ${record.type}:`, error);
            toast.push(m.explorer_thumbnail_open_failed_toast({ type: record.type, error: error.message }), {
                classes: ["toast-danger"],
            });
        } finally {
            operationLoading = false;
        }
    }

    function openProjectFile(description, record){
        // Add folderId to description object
        description.folderId = record.locationId;

        saveLastExplorerLocationFromRecord(userId, record);
        
        setExternalDataState({
            platform: "Project File",
            status : "importing",
            rawDescription: JSON.stringify(description)
        });
        
        //call loadProjectFile modal
        showModal("loadProjectFile");
    }

    async function openTeamProjectFile(description, selectedRecord) {
        try {
            operationLoading = true;
            // Extract team ID and project ID
            const projectTeamId = description.teamId;
            
            if (!projectTeamId || !teamId) {
                throw new Error("Missing team or project ID in file");
            }
            
            // console.log(`Looking up share codes for team project: ${projectTeamId} in team: ${teamId}`);
            
            // Check for project snapshots first (to ensure there's data to share)
            const snapshotsRef = collection(db, "teams", teamId, "projects", projectTeamId, "snapshots");
                
            const snapshotsQuery = await getDocs(query(snapshotsRef, limit(1)));
            
            if (snapshotsQuery.empty) {
                // console.log("No snapshots found for team project, falling back to project file import");
                fallbackToProjectFileImport(description, selectedRecord);
                return;
            }
            
            // Look for share codes that reference this project
            const sharesRef = collection(db, "teams", teamId, "shares");
                
            const sharesQuery = await getDocs(query(sharesRef,
                where("projectTeamId", "==", projectTeamId),
                limit(1)));            
            
            if (sharesQuery.empty) {
                // console.log("No share codes found for team project, falling back to project file import");
                fallbackToProjectFileImport(description, selectedRecord);
                return;
            }
            
            // Get the first share code
            const shareDoc = sharesQuery.docs[0];
            const shareId = shareDoc.id;
            
            // console.log(`Found share code: ${shareId}`);
            
            // Update URL with share code and open teamProjectImport modal
            const url = new URL(window.location.href);
            url.searchParams.set("id", shareId);
            window.history.pushState({}, '', url);
            
            toast.push(m.explorer_thumbnail_open_team_project_toast(), {
                classes: ["toast-info"],
            });

            saveLastExplorerLocationFromRecord(userId, selectedRecord);
            
            showModal("teamProjectImport");
            
        } catch (error) {
            console.error("Error processing team project:", error);
            fallbackToProjectFileImport(description, selectedRecord);
        }
    }
    
    function fallbackToProjectFileImport(description, record) {
        console.log("Falling back to project file import method");
        toast.push(m.explorer_thumbnail_fallback_project_import_toast(), {
            classes: ["toast-info"],
        });
        
        // Add folderId to description object
        description.folderId = record.locationId;

        saveLastExplorerLocationFromRecord(userId, record);
        
        setExternalDataState({
            platform: "Project File",
            status : "importing",
            rawDescription: JSON.stringify(description)
        });
        
        //call loadProjectFile modal
        showModal("loadProjectFile");
    }

    function openWorkOrder(description, record) {
        // Add folderId to description object
        description.folderId = record.locationId;

        saveLastExplorerLocationFromRecord(userId, record);
        
        setExternalDataState({
            platform: "Work Order",
            status : "importing",
            rawDescription: JSON.stringify(description)
        });
        
        //call workOrderImport modal
        showModal("workOrderImport");
    }

    function selectRecord(clickEvent, id) {
        if (clickEvent.ctrlKey || clickEvent.metaKey) {
            if (explorerState.selectedRecords.includes(id)) {
                explorerState.selectedRecords = explorerState.selectedRecords.filter(recordId => recordId !== id);
            } else {
                explorerState.selectedRecords = [...explorerState.selectedRecords, id];
            }
        } else if (clickEvent.shiftKey) {
            const lastSelectedRecord = explorerState.selectedRecords[explorerState.selectedRecords.length - 1];
            const lastSelectedRecordIndex = explorerState.records.findIndex(record => record.id === lastSelectedRecord);
            const currentRecordIndex = explorerState.records.findIndex(record => record.id === id);
            const recordsToSelect = explorerState.records.slice(Math.min(lastSelectedRecordIndex, currentRecordIndex), Math.max(lastSelectedRecordIndex, currentRecordIndex) + 1);
            explorerState.selectedRecords = recordsToSelect.map(record => record.id);
        } else {
            explorerState.selectedRecords = [id];
        }
    }

    // Drag and drop functions
    function handleDragStart(event, id) {
        // Don't allow drag in save mode
        if (explorerState.context === "save") {
            event.preventDefault();
            return;
        }
        
        // If the current item isn't selected, select only this item
        if (!explorerState.selectedRecords.includes(id)) {
            explorerState.selectedRecords = [id];
        }
        
        // Set drag data
        event.dataTransfer.effectAllowed = "move";
        event.dataTransfer.setData("text/plain", JSON.stringify(explorerState.selectedRecords));
        
        // Set a custom class for styling
        const draggedItems = explorerState.selectedRecords.length;
        event.dataTransfer.setData("application/count", draggedItems);
        event.target.classList.add("dragging");
    }
    
    function handleDragEnd(event) {
        event.target.classList.remove("dragging");
        currentDragTarget = null;
    }
    
    function handleDragOver(event, record) {
        // Only allow dropping on folders
        if (record.type !== "folder") {
            return;
        }
        
        // Get the dragged item IDs
        const dragDataString = event.dataTransfer.types.includes('text/plain') ? 
            event.dataTransfer.getData('text/plain') : null;
            
        // If we're dragging the folder onto itself, don't allow drop
        if (dragDataString) {
            try {
                const draggedIds = JSON.parse(dragDataString);
                // Prevent dropping onto self
                if (draggedIds.includes(record.id)) {
                    event.dataTransfer.dropEffect = "none";
                    return;
                }
            } catch (e) {
                // If parsing fails, continue with normal behavior
            }
        }
        
        // Prevent default to allow drop
        event.preventDefault();
        event.dataTransfer.dropEffect = "move";
        
        // Highlight the drop target
        if (currentDragTarget && currentDragTarget !== record.id) {
            const prevElement = document.querySelector(`[data-record-id="${currentDragTarget}"]`);
            if (prevElement) prevElement.classList.remove("drop-target");
        }
        
        event.currentTarget.classList.add("drop-target");
        currentDragTarget = record.id;
    }
    
    function handleDragLeave(event) {
        event.currentTarget.classList.remove("drop-target");
    }
    
    async function handleDrop(event, targetRecord) {
        event.preventDefault();
        event.currentTarget.classList.remove("drop-target");
        
        // Only allow dropping on folders
        if (targetRecord.type !== "folder") {
            return;
        }
        
        try {
            // Get the dragged record IDs
            const recordIds = JSON.parse(event.dataTransfer.getData("text/plain"));
            
            // Don't move if nothing was dragged or trying to move to same folder
            if (!recordIds.length) return;
            
            // Check if trying to drop a folder onto itself
            if (recordIds.includes(targetRecord.id)) {
                toast.push(m.explorer_thumbnail_cannot_move_into_self_toast(), {
                    classes: ["toast-warning"],
                });
                return;
            }
            
            const recordsToMove = explorerState.records.filter(r => recordIds.includes(r.id));
            
            // Check if any of the records are already in the target folder
            const alreadyInFolder = recordsToMove.some(r => r.locationId === targetRecord.id);
            if (alreadyInFolder) {
                toast.push(m.explorer_thumbnail_already_in_folder_toast(), {
                    classes: ["toast-warning"],
                });
                return;
            }
            
            // Confirm move with the user
            await moveRecords(recordsToMove, targetRecord);
            
        } catch (error) {
            // console.error("Error handling drop:", error);
            toast.push(m.explorer_thumbnail_drop_failed_toast({ error: error.message }), {
                classes: ["toast-danger"],
            });
        }
    }
    
    async function moveRecords(records, targetFolder) {
        if (operationLoading) return;
        
        try {
            // Show confirmation dialog
            const itemSuffix = records.length > 1 ? "s" : "";
            const result = await Swal.fire({
                ...SWAL_BASE,
                customClass: SWAL_CLASSES.denyCancel,
                titleText: m.explorer_thumbnail_move_title(),
                text: m.explorer_thumbnail_move_text({
                    count: records.length,
                    suffix: itemSuffix,
                    name: targetFolder.name,
                }),
                showDenyButton: true,
                confirmButtonText: m.explorer_thumbnail_move_confirm(),
                denyButtonText: m.explorer_thumbnail_cancel_button(),
                icon: "question",
            });
            
            if (!result.isConfirmed) return;
            
            operationLoading = true;
            
            // Check if any records are locked
            const lockedRecords = records.filter(r => r.locked && r.lockedBy !== userEmail);
            if (lockedRecords.length > 0) {
                const lockedSuffix = lockedRecords.length > 1 ? "s" : "";
                throw new Error(m.explorer_thumbnail_move_locked_error({ suffix: lockedSuffix }));
            }
            
            // Perform the move operation for each record
            for (const record of records) {
                // Get the appropriate database reference based on the root directory
                const dbRef = record.rootDir === "team" ? teamDbRef : homeDbRef;
                
                // Update the locationId
                await updateDoc(doc(dbRef, record.id), {
                    locationId: targetFolder.id,
                    updatedOn: Timestamp.fromDate(new Date())
                });
                
                // Update local record
                const index = explorerState.records.findIndex(r => r.id === record.id);
                if (index !== -1) {
                    explorerState.records[index].locationId = targetFolder.id;
                }
            }
            
            // Refresh the records list
            await getRecords(explorerState.navHistory[explorerState.navHistory.length - 1]?.id || null);
            
            // Clear selected records
            explorerState.selectedRecords = [];
            
            toast.push(m.explorer_thumbnail_move_success_toast({
                count: records.length,
                suffix: itemSuffix,
                name: targetFolder.name,
            }), {
                classes: ["toast-success"],
            });
            
        } catch (error) {
            // console.error("Error moving records:", error);
            toast.push(m.explorer_thumbnail_move_failed_toast({ error: error.message }), {
                classes: ["toast-danger"],
            });
        } finally {
            operationLoading = false;
        }
    }
    
    // Format date helper function
    function formatDate(timestamp) {
        if (!timestamp) return "-";
        const date = timestamp.toDate();
        return date.toLocaleDateString();
    }
</script>

{#if explorerState.loading}
<div class="loading-container d-flex justify-content-center align-items-center w-100 h-100">
    <Circle
    size="75"
    color="#1eb4b2"
    unit="px"
    duration="1s"
    />
</div>
{:else}
<div id="thumbnail-view" class="container-fluid p-3">
    <!-- Folders Section -->
    {#if folders.length > 0}
    <div class="folders-section mb-3">
        <h6 class="section-title mb-3">{m.explorer_thumbnail_folders_section()}</h6>
        <div class="row row-cols-1 row-cols-md-3 row-cols-lg-4 row-cols-xl-6 g-3">
            {#each folders as folder (folder.uniqueId || folder.id)}
            <div class="col">
                <!-- svelte-ignore a11y_click_events_have_key_events -->
                <!-- svelte-ignore a11y_no_static_element_interactions -->
                <div 
                    class="card folder-card h-100 {explorerState.selectedRecords.includes(folder.id) ? 'selected' : ''}"
                    title="{folder.name}"
                    ondblclick={() => openRecord(folder.id)}
                    onclick={(e) => selectRecord(e, folder.id)}
                    draggable={explorerState.context !== "save"}
                    ondragstart={(e) => handleDragStart(e, folder.id)}
                    ondragend={handleDragEnd}
                    ondragover={(e) => handleDragOver(e, folder)}
                    ondragleave={handleDragLeave}
                    ondrop={(e) => handleDrop(e, folder)}
                    data-record-id={folder.id}
                    data-record-type="folder">
                    <div class="card-body d-flex align-items-center">
                        <i class="bi bi-folder-fill me-2 fs-4 text-warning"></i>
                        <h6 class="card-title mb-0 text-truncate">{folder.name}</h6>
                    </div>
                </div>
            </div>
            {/each}
        </div>
    </div>
    
    <!-- Divider between sections -->
    {#if folders.length > 0 && otherRecords.length > 0}
        <hr class="my-4">
    {/if}
    {/if}
    
    <!-- Other Records Section -->
    {#if otherRecords.length > 0}
    <div class="records-section">
        <h6 class="section-title mb-3">{m.explorer_thumbnail_projects_section()}</h6>
        <div class="row row-cols-1 row-cols-md-2 row-cols-lg-3 row-cols-xl-4 g-3">
            {#each otherRecords as record (record.uniqueId || record.id)}
            <!-- svelte-ignore a11y_no_static_element_interactions -->
             <!-- svelte-ignore a11y_click_events_have_key_events -->
            <div class="col">
                <div 
                    class="card record-card h-100 {explorerState.selectedRecords.includes(record.id) ? 'selected' : ''}"
                    ondblclick={() => openRecord(record.id)}
                    onclick={(e) => selectRecord(e, record.id)}
                    draggable={explorerState.context !== "save"}
                    ondragstart={(e) => handleDragStart(e, record.id)}
                    ondragend={handleDragEnd}
                    data-record-id={record.id}
                    data-record-type={record.type}>
                    <div class="card-header d-flex justify-content-between align-items-start">
                        <div class="d-flex title-container">
                            <i class="bi {iconMap[record.type]} me-2 flex-shrink-0"></i>
                            <span class="record-title fw-bold">{record.name}</span>
                        </div>
                        <div class="record-icons flex-shrink-0 ms-1">
                            {#if record.locked}<i class="bi bi-lock-fill ms-1 text-secondary"></i>{/if}
                            {#if record.star}<i class="bi bi-star-fill ms-1 text-warning"></i>{/if}
                        </div>
                    </div>
                    <div class="card-body">
                        <div class="record-details">
                            {#if record.state}
                            <div class="detail-row">
                                <small class="text-muted">{m.explorer_thumbnail_state_label()}</small>
                                {#if record.state === "Blocked"}
                                    <span class="badge text-bg-danger">Blocked</span>
                                {:else}
                                    <small class="text-truncate">{record.state}</small>
                                {/if}
                            </div>
                            {/if}
                            
                            {#if record.assignedTo}
                            <div class="detail-row">
                                <small class="text-muted">{m.explorer_thumbnail_assigned_to_label()}</small>
                                <small class="text-truncate">{record.assignedTo}</small>
                            </div>
                            {/if}
                            
                            {#if record.eventGroups}
                            <div class="detail-row">
                                <small class="text-muted">{m.explorer_thumbnail_event_groups_label()}</small>
                                <small>{record.eventGroups}</small>
                            </div>
                            {/if}
                            
                            {#if record.totalEvents}
                            <div class="detail-row">
                                <small class="text-muted">{m.explorer_thumbnail_total_events_label()}</small>
                                <small>{record.totalEvents}</small>
                            </div>
                            {/if}
                            
                            <div class="detail-row">
                                <small class="text-muted">{m.explorer_thumbnail_modified_label()}</small>
                                <small>{formatDate(record.updatedOn)}</small>
                            </div>
                            
                            {#if record.owner}
                            <div class="detail-row">
                                <small class="text-muted">{m.explorer_thumbnail_owner_label()}</small>
                                <small class="text-truncate">{record.owner}</small>
                            </div>
                            {/if}
                        </div>
                    </div>
                </div>
            </div>
            {/each}
        </div>
    </div>
    {/if}
    
    <!-- Empty state -->
    {#if explorerState.records.length === 0}
    <div class="empty-state text-center py-5">
        <i class="bi bi-folder-x display-1 text-muted"></i>
        <p class="mt-3 text-muted">{m.explorer_thumbnail_empty_state()}</p>
    </div>
    {/if}
</div>
{/if}

<style lang="css">
    #thumbnail-view {
        height: 80vh;
        overflow-y: auto;
    }
    
    .loading-container {
        height: 80vh;
    }

    .folder-card {
        cursor: pointer;
        transition: all 0.2s ease-in-out;
        border: 1px solid rgba(0, 0, 0, 0.125);
    }
    
    .record-card {
        cursor: pointer;
        transition: all 0.2s ease-in-out;
        border: 1px solid rgba(0, 0, 0, 0.125);
    }
    
    .selected {
        background-color: rgba(13, 110, 253, 0.1);
        border-color: #0d6efd;
        box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
    }
    
    .card.dragging {
        opacity: 0.5;
    }
    
    .card.drop-target {
        border-color: #0d6efd;
        background-color: rgba(13, 110, 253, 0.1);
        box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
    }
    
    .card-header {
        background-color: rgba(0, 0, 0, 0.03);
        border-bottom: 1px solid rgba(0, 0, 0, 0.125);
        padding: 0.75rem;
    }
    
    .detail-row {
        display: flex;
        justify-content: space-between;
        margin-bottom: 0.5rem;
    }
    
    .empty-state {
        color: #6c757d;
    }
    
    .section-title {
        font-weight: 600;
        color: #495057;
    }
    
    .title-container {
        flex: 1;
        min-width: 0; /* Important for proper flex item wrapping */
        align-items: flex-start;
    }
    
    .record-title {
        word-wrap: break-word;
        word-break: break-word;
        hyphens: auto;
        line-height: 1.2;
    }
</style>